/**
 * Multi-line gameplan canvas.
 *
 * Renders every line in the active gameplan side-by-side as columns
 * in a single horizontally-scrollable SVG. The active line is
 * emphasized (brighter, with a highlight band); non-active lines are
 * dimmed but fully interactive — clicking any node selects it, and
 * clicking any receptor opens the picker AND activates that line.
 *
 * Switching the active line (via carousel, header pill, or receptor
 * click) smoothly scrolls the canvas so the new active line is
 * horizontally centered. New-line creation triggers the same scroll.
 *
 * This component owns its own scroll container so the picker can be
 * absolute-positioned inside it and scroll with the content. The
 * editor body no longer wraps the canvas in another overflow:auto.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '../state/store';
import type { Counter, Move, Position } from '../types/domain';
import type { Gameplan, Line, LineNode } from '../types/gameplan';
import { MARGIN_Y, NODE_H, NODE_W, layoutLine } from './gameplan-layout';
import type { LayoutEdge, LayoutNode, LineLayout } from './gameplan-layout';
import { LineElementEditor } from './LineElementEditor';
import type { EditorTarget } from './LineElementEditor';
import { LinePicker } from './LinePicker';
import { useViewport } from './useViewport';

type Props = {
  gameplan: Gameplan;
  activeLineId: string | null;
  positions: Position[];
  moves: Move[];
  counters: Counter[];
};

// Picker dimensions — must match LinePicker.tsx.
const PICKER_W = 240;
const PICKER_GUTTER = 8;
// Element editor popover dimensions — must match LineElementEditor.tsx.
const EDITOR_W = 260;

// FEATURE FLAG — on-canvas notes editing.
// User feedback (mobile UX batch, item #10): "Lets not allow notes
// being added on lines directly to the line canvas for now. It gets
// too cluttered and is already tight enough."
//
// We keep all of the notes data model, the LineElementEditor file,
// and the per-node onEditNode / per-edge onEditEdge wiring intact —
// we just hide the entry-point badges (pencil, edge click-to-edit)
// behind this flag. Pre-existing notes still render via their
// indicator dots and round-trip through the cheatsheet export. Flip
// this back to true to restore the full inline-editor flow.
const ALLOW_ON_CANVAS_NOTES = false;

// Layout constants for the multi-line composition.
// Horizontal gap between line columns. Tightened from 40 → 24 in the
// mobile-polish pass so multiple lines fit in a narrow bottom-sheet
// canvas without forcing the user to scroll between them.
const LINE_GUTTER = 24;
// Line columns no longer carry a header pill (per user feedback the
// pill duplicated the carousel and pushed the first node off-center).
// HEADER_H is kept as 0 so all the y-offset math below stays correct
// without a structural refactor.
const HEADER_H = 0;
const EMPTY_LINE_W = 220; // width allocated to an empty line column

// Visual dimming applied to non-active lines so the active one pops.
const NON_ACTIVE_OPACITY = 0.45;

// Receptor sits this many SVG units below the bottom of its node card.
const RECEPTOR_GAP = 12;
const RECEPTOR_R = 9;

// Length of the arrowhead in SVG units. The path stops this many
// units BEFORE the target node card so the triangle's base meets the
// curve cleanly and its tip lands at the card edge — no "floating
// arrowhead with a gap" rendering artifact from the previous setup.
const ARROW_LEN = 8;

// Picker state — open against either an existing node (extend) or
// at the start of a specific line (start). Both carry the line id so
// the picker can target the right line when committing.
type PickerState =
  | {
      kind: 'extend';
      lineId: string;
      sourceNode: LineNode;
      x: number;
      y: number;
    }
  | {
      kind: 'start';
      lineId: string;
      x: number;
      y: number;
    };

// Per-line laid-out item composited into the canvas.
type LineItem = {
  line: Line;
  layout: LineLayout | null; // null when the line has no nodes
  xOffset: number; // x where this column's local coords begin
  width: number; // total width of this column slot
};

export function LinesCanvas({
  gameplan,
  activeLineId,
  positions,
  moves,
  counters,
}: Props) {
  const setActiveLine = useAppStore((s) => s.setActiveLine);
  const setSelection = useAppStore((s) => s.setSelection);
  const removeLineNode = useAppStore((s) => s.removeLineNode);
  // Touch devices have no hover, so the eye / edit / × badges that
  // are hover-revealed on desktop need to always show. Uses `isTouch`
  // (pointer: coarse media query) instead of `isMobile` (width ≤
  // 768px) because landscape phones have viewport widths above the
  // mobile threshold (iPhone Pro Max landscape is 932px) — the
  // width check would incorrectly treat them as desktop and fall
  // back to hover-reveal that touch can't trigger. `isTouch` is
  // input-modality based, so it covers every no-hover surface:
  // phones (either orientation), tablets, TVs, kiosks.
  const { isTouch } = useViewport();
  const alwaysShowNodeActions = isTouch;
  const enterDrawerFromGameplan = useAppStore(
    (s) => s.enterDrawerFromGameplan,
  );

  // Two-tier active-line model:
  //   - PRIMARY (`activeLineId`, the prop from the store): committed
  //     by a click / tap. Sticky — stays until you click another
  //     line. This is what the canvas auto-scrolls to center.
  //   - HOVER (`hoveredLineId`, local): a TRANSIENT preview. While the
  //     pointer is over a different line, that line reads as active;
  //     the moment the pointer leaves, we pop back to the primary.
  // The visually-active line is `displayLineId = hover ?? primary`.
  // Touch devices never set hover (no hover state), so displayLineId
  // == activeLineId there — mobile behaviour is unchanged.
  const [hoveredLineId, setHoveredLineId] = useState<string | null>(null);
  const displayLineId = hoveredLineId ?? activeLineId;

  // Compute per-line layouts and pack them horizontally.
  const composed = useMemo(() => {
    const items: LineItem[] = [];
    let xOffset = 0;
    for (const line of gameplan.lines) {
      const layout = line.nodes.length > 0 ? layoutLine(line) : null;
      const width = layout ? layout.width : EMPTY_LINE_W;
      items.push({ line, layout, xOffset, width });
      xOffset += width + LINE_GUTTER;
    }
    const totalWidth = Math.max(xOffset - LINE_GUTTER, EMPTY_LINE_W);
    const maxLineHeight = Math.max(
      ...items.map((it) => it.layout?.height ?? 220),
      220,
    );
    return {
      items,
      totalWidth,
      totalHeight: HEADER_H + maxLineHeight + MARGIN_Y,
    };
  }, [gameplan.lines]);

  // Per-line label lookups. The picker resolves labels itself, but
  // the canvas needs them for the node cards.
  const positionsById = useMemo(
    () => new Map(positions.map((p) => [p.id, p])),
    [positions],
  );
  const movesById = useMemo(
    () => new Map(moves.map((m) => [m.id, m])),
    [moves],
  );
  const countersById = useMemo(
    () => new Map(counters.map((c) => [c.id, c])),
    [counters],
  );

  const labelFor = (
    ln: LineNode,
  ): { label: string; missing: boolean; terminal: boolean } => {
    switch (ln.ref.kind) {
      case 'position': {
        const p = positionsById.get(ln.ref.id);
        // Terminal positions (zone 'submitted' = "Game Over",
        // 'scrambled' = chaotic transition) get their own palette so
        // they read distinctly from regular positions in a line.
        const terminal =
          p?.zone === 'submitted' || p?.zone === 'scrambled';
        return { label: p?.name ?? ln.ref.id, missing: !p, terminal };
      }
      case 'move': {
        const m = movesById.get(ln.ref.id);
        return { label: m?.name ?? ln.ref.id, missing: !m, terminal: false };
      }
      case 'counter': {
        const c = countersById.get(ln.ref.id);
        return { label: c?.name ?? ln.ref.id, missing: !c, terminal: false };
      }
    }
  };

  // ----- Hover / picker / scroll state -----
  //
  // Two hover trackers because their reveals scope differently:
  //   - hoveredCardId  — only set/cleared by the card itself; drives
  //                      the × delete badge.
  //   - hoveredForReceptor — set by card, receptor disc, OR any
  //                      outgoing-edge hit overlay belonging to the
  //                      source node; drives the + receptor. Cleared
  //                      lazily so cursor can hop card → edge →
  //                      receptor disc without the + flickering off
  //                      between handoffs.
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [hoveredForReceptor, setHoveredForReceptor] = useState<string | null>(
    null,
  );
  // When the hover came from a specific outgoing edge, remember which
  // one — receptor position then tracks that edge's midpoint instead
  // of always falling back to the last outgoing edge.
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const receptorClearTimer = useRef<number | null>(null);
  const flagReceptor = (
    nodeId: string | null,
    edgeId: string | null = null,
  ) => {
    if (receptorClearTimer.current !== null) {
      window.clearTimeout(receptorClearTimer.current);
      receptorClearTimer.current = null;
    }
    if (nodeId !== null) {
      setHoveredForReceptor(nodeId);
      setHoveredEdgeId(edgeId);
    } else {
      // Tiny debounce so transitions card → edge → receptor don't
      // momentarily blank the +. Clears both node + edge tracking
      // so a stale edgeId can't outlive its node.
      receptorClearTimer.current = window.setTimeout(() => {
        receptorClearTimer.current = null;
        setHoveredForReceptor(null);
        setHoveredEdgeId(null);
      }, 60);
    }
  };
  // Receptor disc hover: keep the existing state alive but don't
  // overwrite hoveredEdgeId — the receptor sits where it sat when
  // entered, so the disc doesn't jump positions mid-reach.
  const keepReceptorAlive = (nodeId: string) => {
    if (receptorClearTimer.current !== null) {
      window.clearTimeout(receptorClearTimer.current);
      receptorClearTimer.current = null;
    }
    setHoveredForReceptor(nodeId);
  };
  useEffect(
    () => () => {
      if (receptorClearTimer.current !== null) {
        window.clearTimeout(receptorClearTimer.current);
      }
    },
    [],
  );

  const [picker, setPicker] = useState<PickerState | null>(null);

  // Editor popover state — node notes or edge label/delete. Stores
  // the target + an anchor in canvas coords (xCenter, y).
  const [editor, setEditor] = useState<
    | null
    | {
        target: EditorTarget;
        x: number;
        y: number;
      }
  >(null);

  // Scrollable wrapper ref — we drive smooth scroll to the active
  // line on switch/create from here.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);
  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    setContainerW(el.clientWidth);
    const ro = new ResizeObserver(() => setContainerW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Center the active line on switch — but only for EXPLICIT
  // activations (carousel pill click, header click, new-line create,
  // receptor click on a different line). Hover-driven activations
  // skip this scroll so the canvas doesn't pan out from under the
  // cursor while the user is browsing.
  const skipNextScrollRef = useRef(false);
  useEffect(() => {
    if (skipNextScrollRef.current) {
      skipNextScrollRef.current = false;
      return;
    }
    if (!wrapperRef.current || activeLineId === null) return;
    const item = composed.items.find((it) => it.line.id === activeLineId);
    if (!item) return;
    const center = item.xOffset + item.width / 2;
    const target = Math.max(0, center - wrapperRef.current.clientWidth / 2);
    wrapperRef.current.scrollTo({ left: target, behavior: 'smooth' });
  }, [activeLineId, composed]);

  // Hover enter/leave — set the TRANSIENT hovered line (preview),
  // never the committed primary. No scroll on hover (the center
  // effect keys on the primary `activeLineId` only), so previewing a
  // line by hovering it doesn't yank the canvas around.
  const onLineHoverEnter = (lineId: string) => {
    setHoveredLineId(lineId);
  };
  const onLineHoverLeave = (lineId: string) => {
    // Guard against enter-B-then-leave-A event ordering: only clear
    // if the line leaving is the one currently previewed.
    setHoveredLineId((prev) => (prev === lineId ? null : prev));
  };

  // Close the picker if the line it was opened on disappears (e.g.
  // the user deletes the line). Saves a stale-state class of bugs.
  useEffect(() => {
    if (!picker) return;
    if (!gameplan.lines.some((l) => l.id === picker.lineId)) {
      setPicker(null);
    }
  }, [gameplan.lines, picker]);

  // Resolving the active line that the picker is bound to.
  const pickerLine = picker
    ? gameplan.lines.find((l) => l.id === picker.lineId) ?? null
    : null;

  // Clamp helper: returns a `left` for an absolute-positioned popover
  // of `width` whose desired center is `centerX`, kept inside the
  // canvas wrapper with `gutter` on each side. Falls back to gutter-
  // left when the canvas is too narrow to fully fit the popover.
  const clampPopoverLeft = (
    centerX: number,
    width: number,
    gutter: number,
  ): number => {
    const desired = centerX - width / 2;
    if (containerW <= 0) return desired;
    const maxLeft = composed.totalWidth - width - gutter;
    const minLeft = gutter;
    if (maxLeft < minLeft) return minLeft;
    return Math.max(minLeft, Math.min(desired, maxLeft));
  };

  const pickerLeft = picker
    ? clampPopoverLeft(picker.x, PICKER_W, PICKER_GUTTER)
    : 0;
  const editorLeft = editor
    ? clampPopoverLeft(editor.x, EDITOR_W, PICKER_GUTTER)
    : 0;

  // If the edit-target line goes away (e.g. user deletes the line
  // mid-edit), close the editor so the popover doesn't dangle.
  useEffect(() => {
    if (!editor) return;
    if (!gameplan.lines.some((l) => l.id === editor.target.lineId)) {
      setEditor(null);
    }
  }, [gameplan.lines, editor]);

  // ----- Click handlers -----

  const onNodeClick = (ln: LineNode) => {
    if (ln.ref.kind === 'counter') {
      setSelection({ kind: 'counter', id: ln.ref.id, moveId: ln.ref.moveId });
    } else {
      setSelection({ kind: ln.ref.kind, id: ln.ref.id });
    }
  };

  const onReceptorClick = (
    item: LineItem,
    laidNode: LayoutNode,
    receptorX: number,
    receptorY: number,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    // Receptor click always activates the line being edited.
    if (activeLineId !== item.line.id) setActiveLine(item.line.id);
    // receptorX/Y are in the line's local coords (post HEADER_H shift
    // and before the line's xOffset translate). Convert to canvas
    // coords for picker anchoring.
    setPicker({
      kind: 'extend',
      lineId: item.line.id,
      sourceNode: laidNode.node,
      x: item.xOffset + receptorX,
      y: HEADER_H + receptorY + RECEPTOR_R + 6,
    });
  };

  const onDeleteNode = (item: LineItem, nodeId: string) => {
    removeLineNode(gameplan.id, item.line.id, nodeId);
    // If the picker was anchored on this node, close it.
    setPicker((cur) =>
      cur && cur.kind === 'extend' && cur.sourceNode.id === nodeId
        ? null
        : cur,
    );
  };

  // Eyeball click — jump to the Detail drawer with this entity
  // selected. Records the gameplan + line as the return target so
  // the drawer's back button comes straight back here.
  const onViewNode = (item: LineItem, ln: LineNode) => {
    const sel =
      ln.ref.kind === 'counter'
        ? { kind: 'counter' as const, id: ln.ref.id, moveId: ln.ref.moveId }
        : { kind: ln.ref.kind, id: ln.ref.id };
    enterDrawerFromGameplan(sel, gameplan.id, item.line.id);
  };

  const onStartHereClick = (item: LineItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeLineId !== item.line.id) setActiveLine(item.line.id);
    const centerX = item.xOffset + item.width / 2;
    const centerY = HEADER_H + (composed.totalHeight - HEADER_H) / 2;
    setPicker({
      kind: 'start',
      lineId: item.line.id,
      x: centerX,
      y: centerY + 28,
    });
  };

  // onHeaderClick removed — the per-column header pill is gone. The
  // line carousel above the canvas now owns line-activation gestures.

  // Open the notes editor for a specific node. Anchors below the
  // node card so the popover doesn't cover the card you're editing.
  const onEditNode = (item: LineItem, laidNode: LayoutNode) => {
    // Toggle: clicking the pencil while THIS node's editor is open
    // closes it instead of bouncing the popover.
    if (
      editor &&
      editor.target.kind === 'node' &&
      editor.target.nodeId === laidNode.node.id &&
      editor.target.lineId === item.line.id
    ) {
      setEditor(null);
      return;
    }
    if (activeLineId !== item.line.id) setActiveLine(item.line.id);
    const node = laidNode.node;
    const label = labelFor(node).label;
    setEditor({
      target: {
        kind: 'node',
        gameplanId: gameplan.id,
        lineId: item.line.id,
        nodeId: node.id,
        initialNotes: node.notes ?? '',
        displayName: label,
      },
      x: item.xOffset + laidNode.x,
      y: HEADER_H + laidNode.y + NODE_H / 2 + 10,
    });
    setPicker(null);
  };

  // Open the edge editor (label + delete). Anchors at the edge
  // midpoint so the popover lands close to the curve being edited.
  const onEditEdge = (
    item: LineItem,
    le: LayoutEdge,
    midX: number,
    midY: number,
  ) => {
    // Same toggle behavior as nodes.
    if (
      editor &&
      editor.target.kind === 'edge' &&
      editor.target.edgeId === le.edge.id &&
      editor.target.lineId === item.line.id
    ) {
      setEditor(null);
      return;
    }
    if (activeLineId !== item.line.id) setActiveLine(item.line.id);
    const fromLabel = labelFor(le.from.node).label;
    const toLabel = labelFor(le.to.node).label;
    setEditor({
      target: {
        kind: 'edge',
        gameplanId: gameplan.id,
        lineId: item.line.id,
        edgeId: le.edge.id,
        initialLabel: le.edge.label ?? '',
        contextHeading: `${fromLabel} → ${toLabel}`,
      },
      x: item.xOffset + midX,
      y: HEADER_H + midY + 14,
    });
    setPicker(null);
  };

  // ----- Render -----

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        overflow: 'auto',
        background: 'rgba(0,0,0,0.20)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 6,
        // Fill whatever vertical space the parent gives us so the
        // editor can hand this component "the rest of the room"
        // without imposing its own scroll above us.
        height: '100%',
        minHeight: 280,
      }}
    >
      <svg
        viewBox={`0 0 ${composed.totalWidth} ${composed.totalHeight}`}
        width={composed.totalWidth}
        height={composed.totalHeight}
        style={{ display: 'block' }}
      >
        <defs>
          {/* Arrowheads — refX=0 so the triangle BASE meets the path
              endpoint exactly (no overlap, no gap). markerUnits is
              userSpaceOnUse so the marker is sized in SVG units
              independent of stroke width, which lets us pull each
              edge's path endpoint back by exactly the marker length
              to land the tip on the target node card. */}
          <marker
            id="bjj-arrow-forward"
            viewBox="0 0 10 10"
            refX="0"
            refY="5"
            markerUnits="userSpaceOnUse"
            markerWidth={ARROW_LEN}
            markerHeight={ARROW_LEN}
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#8a92a0" />
          </marker>
          <marker
            id="bjj-arrow-back"
            viewBox="0 0 10 10"
            refX="0"
            refY="5"
            markerUnits="userSpaceOnUse"
            markerWidth={ARROW_LEN}
            markerHeight={ARROW_LEN}
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#b395d2" />
          </marker>
        </defs>

        {/* Active line highlight band — draw behind everything as a
            soft column-shaped backdrop so the active line reads as
            "the one I'm working on". */}
        {composed.items.map((item) => {
          if (item.line.id !== displayLineId) return null;
          return (
            <rect
              key={`hl-${item.line.id}`}
              x={item.xOffset}
              y={0}
              width={item.width}
              height={composed.totalHeight}
              fill="rgba(155,231,255,0.04)"
              stroke="rgba(155,231,255,0.18)"
              strokeWidth={1}
              rx={6}
              ry={6}
            />
          );
        })}

        {/* Per-line columns: header pill, then either the line graph
            or the "+ start here" affordance for empty columns. */}
        {composed.items.map((item) => {
          const isActive = item.line.id === displayLineId;
          return (
            <g
              key={item.line.id}
              transform={`translate(${item.xOffset} 0)`}
              opacity={isActive ? 1 : NON_ACTIVE_OPACITY}
              // Hover PREVIEWS the line (transient) — pointer enter
              // sets it as the display line, leave pops back to the
              // committed primary. A CLICK / TAP commits it as the
              // primary (sticky) line and triggers the scroll-to-
              // center effect (which keys on activeLineId, the
              // primary, not the hover preview).
              onMouseEnter={() => onLineHoverEnter(item.line.id)}
              onMouseLeave={() => onLineHoverLeave(item.line.id)}
              onClick={() => {
                if (item.line.id !== activeLineId) {
                  setActiveLine(item.line.id);
                }
              }}
            >
              {/* Invisible column-wide hit zone so hover-to-activate
                  fires anywhere in the column, not just on the paint
                  of node cards / edges. Rendered first so all real
                  content paints on top and gets clicks normally. */}
              <rect
                x={0}
                y={0}
                width={item.width}
                height={composed.totalHeight}
                fill="transparent"
                pointerEvents="all"
              />
              {/* LineHeader pill removed — the carousel above the
                  canvas already shows the line name. The first node
                  now sits at the top of its column, centered in the
                  column's horizontal slot. */}
              <g>
                {item.layout ? (
                  <LineGraph
                    layout={item.layout}
                    labelFor={labelFor}
                    alwaysShowActions={alwaysShowNodeActions}
                    // Active-line + alwaysShowActions together mean
                    // "mobile is looking at this line right now" —
                    // we use that to keep the receptor + buttons
                    // visible on every node of the active line so
                    // mobile users (who can't hover) can discover
                    // them. See LineGraph's body for the gating.
                    isActiveLine={isActive}
                    hoveredCardId={hoveredCardId}
                    setHoveredCardId={setHoveredCardId}
                    hoveredForReceptor={hoveredForReceptor}
                    hoveredEdgeId={hoveredEdgeId}
                    flagReceptor={flagReceptor}
                    keepReceptorAlive={keepReceptorAlive}
                    onNodeClick={onNodeClick}
                    onReceptorClick={(ln, rx, ry, e) =>
                      onReceptorClick(item, ln, rx, ry, e)
                    }
                    onDeleteNode={(nodeId) => onDeleteNode(item, nodeId)}
                    onEditNode={(ln) => onEditNode(item, ln)}
                    onEditEdge={(le, mx, my) =>
                      onEditEdge(item, le, mx, my)
                    }
                    onViewNode={(ln) => onViewNode(item, ln.node)}
                    pickerOpenForNodeId={
                      picker?.kind === 'extend' &&
                      picker.lineId === item.line.id
                        ? picker.sourceNode.id
                        : null
                    }
                  />
                ) : (
                  <EmptyLineAffordance
                    item={item}
                    onClick={(e) => onStartHereClick(item, e)}
                    canvasHeight={composed.totalHeight - HEADER_H}
                    pickerOpen={
                      picker?.kind === 'start' &&
                      picker.lineId === item.line.id
                    }
                  />
                )}
              </g>
            </g>
          );
        })}
      </svg>

      {/* Picker overlay. Positioned absolutely inside the scrollable
          wrapper so it scrolls with the content. */}
      {picker && pickerLine && (
        <div
          style={{
            position: 'absolute',
            top: picker.y,
            left: pickerLeft,
          }}
        >
          <LinePicker
            source={
              picker.kind === 'start'
                ? { mode: 'start' }
                : { mode: 'extend', sourceNode: picker.sourceNode }
            }
            gameplanId={gameplan.id}
            line={pickerLine}
            positions={positions}
            moves={moves}
            counters={counters}
            onClose={() => setPicker(null)}
          />
        </div>
      )}

      {/* Element editor overlay (node notes or edge label/delete).
          Same positioning model as the picker. */}
      {editor && (
        <div
          style={{
            position: 'absolute',
            top: editor.y,
            left: editorLeft,
          }}
        >
          <LineElementEditor
            target={editor.target}
            onClose={() => setEditor(null)}
          />
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// (LineHeader pill removed — the carousel above the canvas owns
//  line naming and the canvas now just shows the graph itself.)
// -----------------------------------------------------------------

// -----------------------------------------------------------------
// One line's graph (nodes + edges + receptors)
// -----------------------------------------------------------------

function LineGraph({
  layout,
  labelFor,
  alwaysShowActions,
  isActiveLine,
  hoveredCardId,
  setHoveredCardId,
  hoveredForReceptor,
  hoveredEdgeId,
  flagReceptor,
  keepReceptorAlive,
  onNodeClick,
  onReceptorClick,
  onDeleteNode,
  onEditNode,
  onEditEdge,
  onViewNode,
  pickerOpenForNodeId,
}: {
  layout: LineLayout;
  labelFor: (ln: LineNode) => {
    label: string;
    missing: boolean;
    terminal: boolean;
  };
  /** When true (mobile portrait), the eye / edit / × badges are
   *  always rendered instead of being hover-gated. Touch devices
   *  can't hover, so the badges would otherwise be unreachable. */
  alwaysShowActions: boolean;
  /** True when this LineGraph is rendering the currently-active
   *  line. We use it (in concert with alwaysShowActions) to keep
   *  the + receptor visible on every node of the active line for
   *  touch users. We don't reveal receptors on inactive lines —
   *  those stay quiet so the active line reads clearly. */
  isActiveLine: boolean;
  hoveredCardId: string | null;
  setHoveredCardId: (id: string | null) => void;
  hoveredForReceptor: string | null;
  hoveredEdgeId: string | null;
  flagReceptor: (nodeId: string | null, edgeId?: string | null) => void;
  keepReceptorAlive: (nodeId: string) => void;
  onNodeClick: (ln: LineNode) => void;
  onReceptorClick: (
    ln: LayoutNode,
    receptorX: number,
    receptorY: number,
    e: React.MouseEvent,
  ) => void;
  onDeleteNode: (nodeId: string) => void;
  onEditNode: (ln: LayoutNode) => void;
  onEditEdge: (le: LayoutEdge, midX: number, midY: number) => void;
  onViewNode: (ln: LayoutNode) => void;
  pickerOpenForNodeId: string | null;
}) {
  // Pre-bin outgoing layout edges per source node so we don't scan
  // the full edge list for each node every render.
  const outgoingByNode = new Map<string, typeof layout.edges>();
  for (const le of layout.edges) {
    const arr = outgoingByNode.get(le.from.node.id);
    if (arr) arr.push(le);
    else outgoingByNode.set(le.from.node.id, [le]);
  }

  return (
    <>
      {/* Edges first (behind nodes). Each edge has an invisible
          thick-stroke hit overlay so the user can hover the edge to
          reveal the source node's + receptor — positioned on the
          midpoint of THIS specific edge. Clicking the edge curve
          opens the edge label/delete editor. */}
      <g>
        {layout.edges.map((le) => {
          const mid = edgeMidpoint(le);
          return (
            <EdgePath
              key={le.edge.id}
              le={le}
              hasLabel={!!le.edge.label}
              onEdgeEnter={() =>
                flagReceptor(le.from.node.id, le.edge.id)
              }
              onEdgeLeave={() => flagReceptor(null)}
              onEdgeClick={(e) => {
                e.stopPropagation();
                // Edge label editor is gated behind ALLOW_ON_CANVAS_
                // NOTES so the canvas stays uncluttered. Edges can
                // still be deleted via their × badge; only the
                // label-editing entry point is suppressed.
                if (ALLOW_ON_CANVAS_NOTES) {
                  onEditEdge(le, mid.x, mid.y);
                }
              }}
            />
          );
        })}
      </g>

      {/* Inline edge labels — rendered ABOVE the edge curves and
          BELOW the node group so the labels read clearly without
          covering node cards. Slight offset above the midpoint so
          the + receptor (when shown) doesn't sit on top of them. */}
      <g pointerEvents="none">
        {layout.edges.map((le) => {
          if (!le.edge.label) return null;
          const mid = edgeMidpoint(le);
          return (
            <EdgeLabel
              key={`lbl-${le.edge.id}`}
              x={mid.x}
              y={mid.y - 14}
              text={le.edge.label}
            />
          );
        })}
      </g>

      {/* Nodes + receptors + delete-on-hover. */}
      <g>
        {layout.nodes.map((ln) => {
          const { label, missing, terminal } = labelFor(ln.node);
          const outs = outgoingByNode.get(ln.node.id) ?? [];
          // Receptor position:
          //  1. If the user is hovering a SPECIFIC outgoing edge of
          //     this node, anchor the + on that edge's midpoint.
          //  2. Otherwise fall back to the LAST outgoing edge — gives
          //     a stable default when the receptor is held alive on
          //     the disc itself (between hovers).
          //  3. Leaf nodes (no outgoing) dangle the + below the card
          //     with a stub connector.
          const isLeaf = outs.length === 0;
          const hoveredOwnEdge =
            hoveredEdgeId !== null
              ? outs.find((o) => o.edge.id === hoveredEdgeId) ?? null
              : null;
          const recPos = isLeaf
            ? null
            : edgeMidpoint(hoveredOwnEdge ?? outs[outs.length - 1]);
          const fallback = {
            x: ln.x,
            y: ln.y + NODE_H / 2 + RECEPTOR_GAP + RECEPTOR_R,
          };
          const receptor = recPos ?? fallback;

          // Show the + when this node is the current receptor-target
          // OR when its picker is open. The flagReceptor pipeline
          // sets the target from card/edge/receptor hovers.
          //
          // Touch override: when the viewport is MOBILE (either
          // orientation) AND this is the active line, we ignore the
          // hover pipeline and ALWAYS show the + on every node.
          // Touch devices have no hover state, so the hover-gated
          // reveal would be unreachable.
          const touchAlwaysShow = alwaysShowActions && isActiveLine;
          const showReceptor =
            touchAlwaysShow ||
            hoveredForReceptor === ln.node.id ||
            pickerOpenForNodeId === ln.node.id;
          const receptorLit = pickerOpenForNodeId === ln.node.id;

          // × visibility:
          //   - desktop: card-hover (no debounce — leaving hides ×
          //     immediately so the badge can't "stick");
          //   - mobile (either orientation): ALWAYS visible, because
          //     touch devices have no hover state to drive the reveal.
          const showDelete = alwaysShowActions || hoveredCardId === ln.node.id;

          return (
            <g key={ln.node.id}>
              <NodeCard
                ln={ln}
                label={label}
                kind={ln.node.ref.kind}
                missing={missing}
                terminal={terminal}
                onClick={() => onNodeClick(ln.node)}
                onMouseEnter={() => {
                  setHoveredCardId(ln.node.id);
                  // Card hover only reveals + for LEAF nodes — there's
                  // no edge yet to reveal it via, so the card is the
                  // only discoverable entry point. For non-leaves the
                  // + is on a real connector line; the user reveals
                  // it by hovering the line itself.
                  if (isLeaf) flagReceptor(ln.node.id);
                }}
                onMouseLeave={() => {
                  setHoveredCardId(null);
                  if (isLeaf) flagReceptor(null);
                }}
              />
              {showReceptor && (
                <Receptor
                  cx={receptor.x}
                  cy={receptor.y}
                  isLeaf={isLeaf}
                  nodeBottomY={ln.y + NODE_H / 2}
                  nodeCenterX={ln.x}
                  active={receptorLit}
                  hovered={hoveredCardId === ln.node.id}
                  // keepReceptorAlive preserves the hoveredEdgeId so
                  // the + doesn't jump from the hovered-edge midpoint
                  // to the last-edge midpoint while the cursor is on
                  // the disc.
                  onMouseEnter={() => keepReceptorAlive(ln.node.id)}
                  onMouseLeave={() => flagReceptor(null)}
                  onClick={(e) =>
                    onReceptorClick(ln, receptor.x, receptor.y, e)
                  }
                />
              )}
              {showDelete && (
                <>
                  {/* Corner controls — view (eye) · [edit pencil,
                      currently hidden] · delete (×) — laid out
                      left → right across the top of the card. Each
                      shares the same hand-off pattern: their own
                      mouseenter/leave re-assert hoveredCardId so
                      cursor handoff between siblings doesn't blink
                      the row off.
                      The pencil is suppressed by
                      ALLOW_ON_CANVAS_NOTES (item #10 in the mobile
                      UX batch); when hidden the eye/× pair shifts
                      to a tighter two-badge layout so the corner
                      doesn't read as having a missing slot. */}
                  <EyeBadge
                    cx={
                      ln.x +
                      NODE_W / 2 -
                      (ALLOW_ON_CANVAS_NOTES ? 46 : 28)
                    }
                    cy={ln.y - NODE_H / 2 + 10}
                    onMouseEnter={() => setHoveredCardId(ln.node.id)}
                    onMouseLeave={() => setHoveredCardId(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewNode(ln);
                    }}
                  />
                  {ALLOW_ON_CANVAS_NOTES && (
                    <EditBadge
                      cx={ln.x + NODE_W / 2 - 28}
                      cy={ln.y - NODE_H / 2 + 10}
                      onMouseEnter={() => setHoveredCardId(ln.node.id)}
                      onMouseLeave={() => setHoveredCardId(null)}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditNode(ln);
                      }}
                    />
                  )}
                  <DeleteBadge
                    cx={ln.x + NODE_W / 2 - 10}
                    cy={ln.y - NODE_H / 2 + 10}
                    onMouseEnter={() => setHoveredCardId(ln.node.id)}
                    onMouseLeave={() => setHoveredCardId(null)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteNode(ln.node.id);
                    }}
                  />
                </>
              )}
              {/* Notes-present indicator — a small bottom-right dot
                  visible whenever the node has notes set, regardless
                  of hover. Lets the user spot annotated steps at a
                  glance without opening every editor. */}
              {!!ln.node.notes && (
                <circle
                  cx={ln.x + NODE_W / 2 - 8}
                  cy={ln.y + NODE_H / 2 - 8}
                  r={3}
                  fill="rgba(155,231,255,0.85)"
                  pointerEvents="none"
                />
              )}
            </g>
          );
        })}
      </g>
    </>
  );
}

// -----------------------------------------------------------------
// Edge midpoint helper — returns the (x, y) on the bezier curve at
// t=0.5 for forward and back edges. The forward bezier I draw
// degenerates to the straight midpoint; the back-edge curve bulges
// right by 0.75 * bulge.
// -----------------------------------------------------------------

function edgeMidpoint(le: LayoutEdge): { x: number; y: number } {
  const { from, to, kind } = le;
  if (kind === 'forward') {
    // Path endpoints (matching EdgePath below): the curve stops
    // ARROW_LEN units before the target node card so the arrowhead's
    // base meets the curve and its tip lands on the card edge.
    const y1 = from.y + NODE_H / 2;
    const y2 = to.y - NODE_H / 2 - ARROW_LEN;
    return {
      x: (from.x + to.x) / 2,
      y: (y1 + y2) / 2,
    };
  }
  // Back edge: anchored on the right edges of source/target with
  // control points pushed bulge units right; the path again stops
  // ARROW_LEN units before the right edge of the target.
  const bulge = 40 + Math.abs(from.layer - to.layer) * 8;
  const x1 = from.x + NODE_W / 2;
  const x2 = to.x + NODE_W / 2 + ARROW_LEN;
  return {
    x: (x1 + x2) / 2 + 0.75 * bulge,
    y: (from.y + to.y) / 2,
  };
}

// -----------------------------------------------------------------
// Empty-line "+ start here" affordance
// -----------------------------------------------------------------

function EmptyLineAffordance({
  item,
  onClick,
  canvasHeight,
  pickerOpen,
}: {
  item: LineItem;
  onClick: (e: React.MouseEvent) => void;
  canvasHeight: number;
  pickerOpen: boolean;
}) {
  const cx = item.width / 2;
  const cy = canvasHeight / 2;
  const w = 130;
  const h = 36;
  return (
    <g style={{ cursor: 'pointer' }} onClick={onClick}>
      <rect
        x={cx - w / 2}
        y={cy - h / 2}
        width={w}
        height={h}
        rx={8}
        ry={8}
        fill={
          pickerOpen
            ? 'rgba(155,231,255,0.22)'
            : 'rgba(155,231,255,0.10)'
        }
        stroke={
          pickerOpen
            ? 'rgba(155,231,255,0.80)'
            : 'rgba(155,231,255,0.40)'
        }
        strokeWidth={1.2}
        strokeDasharray="4 3"
      />
      <text
        x={cx}
        y={cy + 4}
        textAnchor="middle"
        fontSize={12}
        fontWeight={600}
        fill="#cdf6ff"
      >
        + start here
      </text>
      <text
        x={cx}
        y={cy + h / 2 + 18}
        textAnchor="middle"
        fontSize={10}
        opacity={0.55}
        fill="#cdd2da"
      >
        empty line
      </text>
    </g>
  );
}

// -----------------------------------------------------------------
// Node card
// -----------------------------------------------------------------

function NodeCard({
  ln,
  label,
  kind,
  missing,
  terminal,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  ln: LayoutNode;
  label: string;
  kind: 'position' | 'move' | 'counter';
  missing: boolean;
  terminal: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const palette = kindPalette(kind, terminal);
  const x = ln.x - NODE_W / 2;
  const y = ln.y - NODE_H / 2;

  return (
    <g
      style={{ cursor: 'pointer' }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <rect
        x={x}
        y={y}
        width={NODE_W}
        height={NODE_H}
        rx={8}
        ry={8}
        fill={palette.fill}
        stroke={missing ? '#a04040' : palette.stroke}
        strokeWidth={1.2}
      />
      <text
        x={x + 8}
        y={y + 12}
        fontSize={8}
        fill={palette.tag}
        style={{
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        }}
      >
        {kind}
      </text>
      <text
        x={ln.x}
        y={ln.y + 8}
        textAnchor="middle"
        fontSize={11}
        fontWeight={600}
        fill={palette.text}
      >
        {truncate(label, 20)}
      </text>
    </g>
  );
}

// -----------------------------------------------------------------
// Receptor
// -----------------------------------------------------------------

function Receptor({
  cx,
  cy,
  isLeaf,
  nodeBottomY,
  nodeCenterX,
  active,
  hovered,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  cx: number;
  cy: number;
  /** When true the receptor dangles below the node (no outgoing edge
   *  yet) — we draw a short dashed connector from node-bottom to the
   *  receptor so the visual association reads. When false, the
   *  receptor sits ON an existing edge curve and needs no extra
   *  connector. */
  isLeaf: boolean;
  nodeBottomY: number;
  nodeCenterX: number;
  /** Brighter when this receptor's picker is currently open. */
  active: boolean;
  /** Whether the parent node card is hovered — we bump brightness
   *  slightly so the + reads as "associated with the card you're
   *  on". */
  hovered: boolean;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  // Bright when active OR hovered (so midpoint receptors light up
  // alongside their parent node).
  const lit = active || hovered;
  return (
    <g
      style={{ cursor: 'pointer' }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isLeaf && (
        <line
          x1={nodeCenterX}
          y1={nodeBottomY}
          x2={cx}
          y2={cy - RECEPTOR_R}
          stroke={lit ? 'rgba(155,231,255,0.55)' : 'rgba(155,231,255,0.30)'}
          strokeWidth={1}
          strokeDasharray="2 2"
        />
      )}
      {/* Enlarged transparent hit area so the disc is easy to click
          even when its visible radius is small. */}
      <circle
        cx={cx}
        cy={cy}
        r={RECEPTOR_R + 6}
        fill="transparent"
        pointerEvents="all"
      />
      <circle
        cx={cx}
        cy={cy}
        r={RECEPTOR_R}
        fill={lit ? 'rgba(155,231,255,0.22)' : 'rgba(155,231,255,0.10)'}
        stroke={lit ? 'rgba(155,231,255,0.80)' : 'rgba(155,231,255,0.45)'}
        strokeWidth={1.2}
      />
      <text
        x={cx}
        y={cy + 3.5}
        textAnchor="middle"
        fontSize={12}
        fontWeight={700}
        fill="#cdf6ff"
        // Title-like aria text so screen readers / tooltips read it.
        pointerEvents="none"
      >
        +
      </text>
    </g>
  );
}

// -----------------------------------------------------------------
// Delete × badge (top-right of card, on hover)
// -----------------------------------------------------------------

function DeleteBadge({
  cx,
  cy,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  cx: number;
  cy: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const r = 7;
  return (
    <g
      style={{ cursor: 'pointer' }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r + 4}
        fill="transparent"
        pointerEvents="all"
      />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="rgba(255,128,140,0.18)"
        stroke="rgba(255,128,140,0.55)"
        strokeWidth={1}
      />
      <text
        x={cx}
        y={cy + 3.5}
        textAnchor="middle"
        fontSize={10}
        fontWeight={700}
        fill="#ffc0c8"
        pointerEvents="none"
      >
        ×
      </text>
    </g>
  );
}

// -----------------------------------------------------------------
// Edit pencil badge (sibling of DeleteBadge)
// -----------------------------------------------------------------

function EyeBadge({
  cx,
  cy,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  cx: number;
  cy: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const r = 7;
  return (
    <g
      style={{ cursor: 'pointer' }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <circle
        cx={cx}
        cy={cy}
        r={r + 4}
        fill="transparent"
        pointerEvents="all"
      />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="rgba(155,231,255,0.18)"
        stroke="rgba(155,231,255,0.55)"
        strokeWidth={1}
      />
      {/* Simplified eye: an outer lens almond + an inner pupil dot.
          Tiny path keeps it readable inside r=7. */}
      <path
        d={`M ${cx - 4} ${cy} Q ${cx} ${cy - 3} ${cx + 4} ${cy} Q ${cx} ${cy + 3} ${cx - 4} ${cy} Z`}
        fill="none"
        stroke="#cdf6ff"
        strokeWidth={1}
        pointerEvents="none"
      />
      <circle
        cx={cx}
        cy={cy}
        r={1.2}
        fill="#cdf6ff"
        pointerEvents="none"
      />
    </g>
  );
}

function EditBadge({
  cx,
  cy,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  cx: number;
  cy: number;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const r = 7;
  return (
    <g
      style={{ cursor: 'pointer' }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // Opt out of LineElementEditor's outside-click close: this badge
      // is a toggle, so its own onClick decides open vs close.
      data-bjj-no-autoclose="true"
    >
      <circle
        cx={cx}
        cy={cy}
        r={r + 4}
        fill="transparent"
        pointerEvents="all"
      />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="rgba(155,231,255,0.18)"
        stroke="rgba(155,231,255,0.55)"
        strokeWidth={1}
      />
      {/* Tiny pencil glyph — a slanted stroke + dot, rendered with
          paths so we don't need an icon font in this codebase. */}
      <path
        d={`M ${cx - 3} ${cy + 2} L ${cx + 2} ${cy - 3} M ${cx - 3} ${cy + 2} L ${cx - 3.5} ${cy + 3} M ${cx + 2} ${cy - 3} L ${cx + 3} ${cy - 3.5}`}
        stroke="#cdf6ff"
        strokeWidth={1.2}
        strokeLinecap="round"
        fill="none"
        pointerEvents="none"
      />
    </g>
  );
}

// -----------------------------------------------------------------
// Edge label chip — small rounded background + text near midpoint.
// -----------------------------------------------------------------

function EdgeLabel({
  x,
  y,
  text,
}: {
  x: number;
  y: number;
  text: string;
}) {
  // Width estimate is rough — at 9px font, ~5px/char is close enough.
  const trimmed = text.length > 32 ? text.slice(0, 31) + '…' : text;
  const w = Math.min(Math.max(trimmed.length * 5.4 + 12, 36), 180);
  return (
    <g>
      <rect
        x={x - w / 2}
        y={y - 9}
        width={w}
        height={16}
        rx={6}
        ry={6}
        fill="rgba(20,22,30,0.92)"
        stroke="rgba(155,231,255,0.30)"
        strokeWidth={0.8}
      />
      <text
        x={x}
        y={y + 2.5}
        textAnchor="middle"
        fontSize={9}
        fill="#cdf6ff"
        pointerEvents="none"
      >
        {trimmed}
      </text>
    </g>
  );
}

// -----------------------------------------------------------------
// Edge path
// -----------------------------------------------------------------

function EdgePath({
  le,
  hasLabel,
  onEdgeEnter,
  onEdgeLeave,
  onEdgeClick,
}: {
  le: LayoutEdge;
  hasLabel: boolean;
  onEdgeEnter: () => void;
  onEdgeLeave: () => void;
  onEdgeClick: (e: React.MouseEvent) => void;
}) {
  const { from, to, kind } = le;
  const d =
    kind === 'forward'
      ? (() => {
          // End the curve ARROW_LEN units short of the target card so
          // the arrowhead's BASE meets the curve cleanly and its TIP
          // lands on the card edge.
          const x1 = from.x;
          const y1 = from.y + NODE_H / 2;
          const x2 = to.x;
          const y2 = to.y - NODE_H / 2 - ARROW_LEN;
          const midY = (y1 + y2) / 2;
          return `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
        })()
      : (() => {
          // Same arrowhead alignment for back edges — end the curve
          // ARROW_LEN units to the right of the target's right edge.
          const x1 = from.x + NODE_W / 2;
          const y1 = from.y;
          const x2 = to.x + NODE_W / 2 + ARROW_LEN;
          const y2 = to.y;
          const bulge = 40 + Math.abs(from.layer - to.layer) * 8;
          const cx1 = x1 + bulge;
          const cy1 = y1;
          const cx2 = x2 + bulge;
          const cy2 = y2;
          return `M ${x1} ${y1} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${x2} ${y2}`;
        })();

  return (
    <g
      onMouseEnter={onEdgeEnter}
      onMouseLeave={onEdgeLeave}
      onClick={onEdgeClick}
      style={{ cursor: 'pointer' }}
      // Same toggle opt-out as the pencil — clicking an edge with
      // its editor already open closes it instead of bouncing.
      data-bjj-no-autoclose="true"
    >
      {/* Invisible thick stroke for forgiving hover detection along
          the curve. pointerEvents="stroke" means the hit area is just
          the stroke band (no fill area) so it doesn't intercept clicks
          on nodes underneath. */}
      <path
        d={d}
        fill="none"
        stroke="transparent"
        strokeWidth={16}
        pointerEvents="stroke"
      />
      {/* Visible path. When the edge has a label set we slightly
          brighten the stroke so labeled edges stand out at a glance. */}
      <path
        d={d}
        fill="none"
        stroke={
          kind === 'forward'
            ? hasLabel
              ? '#b8c0cc'
              : '#8a92a0'
            : '#b395d2'
        }
        strokeWidth={hasLabel ? 1.7 : 1.4}
        strokeDasharray={kind === 'back' ? '4 3' : undefined}
        opacity={0.85}
        markerEnd={
          kind === 'forward'
            ? 'url(#bjj-arrow-forward)'
            : 'url(#bjj-arrow-back)'
        }
        pointerEvents="none"
      />
    </g>
  );
}

// -----------------------------------------------------------------
// Style helpers
// -----------------------------------------------------------------

function kindPalette(
  kind: 'position' | 'move' | 'counter',
  terminal = false,
) {
  switch (kind) {
    case 'position':
      // Terminal positions ("Game Over" via zone=submitted, "Scrambled"
      // via zone=scrambled) get a violet palette so they stand apart
      // from regular cyan positions — matches the terminal cluster
      // color (#a48eb0) used by the main canvas.
      if (terminal) {
        return {
          fill: 'rgba(164,142,176,0.14)',
          stroke: 'rgba(164,142,176,0.50)',
          text: '#e2cfee',
          tag: 'rgba(220,180,235,0.75)',
        };
      }
      return {
        fill: 'rgba(155,231,255,0.10)',
        stroke: 'rgba(155,231,255,0.35)',
        text: '#cdf6ff',
        tag: 'rgba(155,231,255,0.55)',
      };
    case 'move':
      return {
        fill: 'rgba(255,200,120,0.10)',
        stroke: 'rgba(255,200,120,0.35)',
        text: '#ffd9a8',
        tag: 'rgba(255,200,120,0.6)',
      };
    case 'counter':
      return {
        fill: 'rgba(255,128,140,0.12)',
        stroke: 'rgba(255,128,140,0.40)',
        text: '#ffc0c8',
        tag: 'rgba(255,128,140,0.65)',
      };
  }
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}
